import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {GraphQLNamedType, GraphQLSchema, SelectionSetNode} from 'query-api-graphql';
import React from 'react';
import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import GraphQLFieldSelector from 'query-api/graphql-field-selector/GraphQLFieldSelector';
import {GraphQLField} from 'query-api/graphql-field-selector/GraphQLFieldSelectorTypes';
import {
  FONT_PROXIMA_11,
  FONT_PROXIMA_13,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Checkbox from 'ui-library/Checkbox';
import {GRAY, GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import sortedBy from 'util/sortedBy';

/** @package */
export interface GraphQLFieldSelectorWrapperProps {
  field: GraphQLField;
  graphQLSchema: GraphQLSchema;
  logicalDepthLimit: number;
  selectionSet: SelectionSetNode;
  setSelectionSet: (newSelectionSet: SelectionSetNode) => void;
  getFieldDisplayName: (field: GraphQLField) => string;
  getTypeDisplayName: (type: GraphQLNamedType) => string;
  isArchivedField: (field: GraphQLField) => boolean;
  isBacklinkField: (field: GraphQLField) => boolean;
  isUserDefinedField: (field: GraphQLField) => boolean;
  isUserComputedField: (field: GraphQLField) => boolean;
}

const ORDERED_PRIORITIZED_SYSTEM_FIELD_NAMES = ['name', 'aliases'];

/**
 * Render a `GraphQLFieldSelector` with a header indicating how many columns have been selected.
 */
export default function GraphQLFieldSelectorWrapper({
  field,
  graphQLSchema,
  logicalDepthLimit,
  selectionSet,
  setSelectionSet,
  getFieldDisplayName,
  getTypeDisplayName,
  isArchivedField,
  isBacklinkField,
  isUserDefinedField,
  isUserComputedField,
}: GraphQLFieldSelectorWrapperProps): JSX.Element {
  const numSelected = countSelectedFields(selectionSet.selections);

  /**
   * Sort and filter fields for display.
   *
   * Order is:
   * - Prioritized system fields that exist in the order defined in ORDERED_PRIORITIZED_SYSTEM_FIELD_NAMES
   * - User-defined fields sorted alphabetically by field display name
   * - Other System-defined fields sorted alphabetically by field display name
   *
   * Removes backlink fields.
   */
  function getSortedFilteredFields(fields: Array<GraphQLField>): Array<GraphQLField> {
    const [userFields, systemFields] = _.partition(fields, isUserDefinedField);
    // Do not render fields derived from computed schema fields.
    const nonComputedUserFields = userFields.filter((field) => !isUserComputedField(field));
    const [archivedUserFields, unarchivedUserFields] = _.partition(nonComputedUserFields, isArchivedField);
    const [prioritizedSystemFields, nonPrioritizedSystemFields] = _.partition(systemFields, (field) =>
      ORDERED_PRIORITIZED_SYSTEM_FIELD_NAMES.includes(field.name)
    );

    const sortedFields = [
      ...sortedBy(prioritizedSystemFields, [
        (field) => ORDERED_PRIORITIZED_SYSTEM_FIELD_NAMES.indexOf(field.name),
      ]),
      ...sortedBy(unarchivedUserFields, [getFieldDisplayName]),
      ...sortedBy(nonPrioritizedSystemFields, [getFieldDisplayName]),
      ...sortedBy(archivedUserFields, [getFieldDisplayName]),
    ];

    return sortedFields.filter((field) => !isBacklinkField(field));
  }

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.header)}>
        <div className={css(styles.title)}>Select columns</div>
        <div className={css(styles.numSelected)}>
          {numSelected} {pluralize(numSelected, 'column')} selected
        </div>
      </div>
      <div className={classNames(css(styles.selectorWrapper), 'scrollbar-override')}>
        <GraphQLFieldSelector
          graphQLSchema={graphQLSchema}
          logicalDepthLimit={logicalDepthLimit}
          getSortedFilteredFields={getSortedFilteredFields}
          selectionSet={selectionSet}
          onChangeSelectionSet={setSelectionSet}
          // Name from packages/benchling-domain-graph/benchling_domain_graph/sdl/schemas/domain_graph_header.graphql
          getIsTypeDisabled={(type) => type.name === 'Inaccessible'}
          renderCheckbox={(isChecked) => <Checkbox checked={isChecked} />}
          renderExpandIcon={(isExpanded, {atMaxDepth, isTypeDisabled}) => (
            <Tooltip
              title={atMaxDepth ? 'Maximum depth reached' : isTypeDisabled ? 'Field type not supported' : ''}
              disabled={!(atMaxDepth || isTypeDisabled)}
            >
              <BenchlingIcon
                icon={isExpanded ? 'triangle-down' : 'triangle-right'}
                className={classNames((atMaxDepth || isTypeDisabled) && css(styles.expandIconDisabled))}
              />
            </Tooltip>
          )}
          // TODO(BNCH-54211): Once BNCH-51735 lands, implement this using the typedef of
          // `getNamedType(field.type)`.
          renderFieldTypeIcon={undefined}
          rootField={field}
          renderFieldName={(field) =>
            isArchivedField(field) ? `${getFieldDisplayName(field)} (archived)` : getFieldDisplayName(field)
          }
          renderTypeName={getTypeDisplayName}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    maxHeight: 300,
    borderRadius: '4px 4px 0px 0px',
    border: `1px solid ${GRAY_LIGHT}`,
    display: 'flex',
    flexDirection: 'column',
  },
  selectorWrapper: {
    ...FONT_PROXIMA_13,
    flexGrow: 1,
    overflowY: 'auto',
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    background: GRAY_LIGHT,
    padding: '7px 10px',
  },
  title: {
    ...FONT_PROXIMA_11,
    color: GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    textTransform: 'uppercase',
  },
  numSelected: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  expandIconDisabled: {
    cursor: 'not-allowed',
    color: GRAY,
  },
});
